import { ActionIcon, Anchor, Group, Text } from '@mantine/core';

import RestaurantProgress from 'components/RestaurantProgress';
import { LOCATION_PATH } from 'constants/path';
import { HeadquarterContext } from 'context/HeadquarterContext';
import { getMatchesBelowProtectedLayout } from 'helpers/routeMatches';
import { useAuth } from 'hooks/useAuth';
import { useCubohActionBar } from 'hooks/useCubohActionBar';
import { useGetLocationHomePath } from 'hooks/useGetLocationHomePath';
import { STATUSES } from 'hooks/useLocation';
import { useContext } from 'react';
import { NavLink, useLocation, useMatches } from 'react-router';
import { ArrowLeftIconMd } from '@chownow/pantry/icons';

const BACK_LABEL = 'Back';

export default function NavGlobalMobile() {
  const { pathname } = useLocation();
  const matches = useMatches();
  const routesBelowLayout = getMatchesBelowProtectedLayout(matches);
  const { isUserCubohOnly } = useAuth();
  const { singleSelectedCompany } = useCubohActionBar();
  const { headquarter, location, locationStatus } = useContext(HeadquarterContext);
  const getLocationHomePath = useGetLocationHomePath();

  const hqName = headquarter?.name ?? 'Headquarters';
  // For Cuboh users, use the company name from singleSelectedCompany if available
  const cubohHqName = isUserCubohOnly && singleSelectedCompany ? singleSelectedCompany.name : null;
  const displayHqName = cubohHqName || hqName;

  const isHqLevelPage = !pathname.includes(LOCATION_PATH);
  // A page with its own headquarter selector opts out, so the nav cannot show a stale second one.
  const hidesHqName = matches.some(
    match => (match.handle as { hideGlobalHqName?: boolean } | undefined)?.hideGlobalHqName,
  );
  const selectedLocation = isUserCubohOnly ? singleSelectedCompany : location;

  if (locationStatus === STATUSES.UNAUTHORIZED || !selectedLocation) {
    return null;
  }

  // Check for nested HQ pages (like Edit HQ Details) before general HQ check
  // This allows back button to show for nested pages
  if (isHqLevelPage) {
    // Check if we have multiple route handles (indicating nested route)
    const routeHandles = routesBelowLayout.filter(
      match => match.handle && typeof match.handle === 'object' && 'name' in match.handle,
    );

    // If we have more than one handle (e.g., "HQ Details" and "Edit HQ Details"), show back button
    if (routeHandles.length > 1) {
      const routesWithPaths = routesBelowLayout.filter(match => match.pathname !== '/');
      const currentRouteIndex = routesWithPaths.findIndex(match => match.pathname === pathname);
      const parentRoute = routesWithPaths[currentRouteIndex - 1];

      if (parentRoute) {
        return (
          <Anchor underline="never" component={NavLink} to={parentRoute.pathname}>
            <Group gap="0">
              <ActionIcon>
                <ArrowLeftIconMd />
              </ActionIcon>
              <Text variant="sublabel-sm">{BACK_LABEL}</Text>
            </Group>
          </Anchor>
        );
      }
    }
  }

  if (hidesHqName) {
    return null;
  }

  if (isHqLevelPage) {
    return (
      <Anchor
        styles={{ root: { maxWidth: '175px' } }}
        truncate="end"
        component={NavLink}
        to="/"
        data-testid="HqName"
        underline="never"
        end
      >
        {displayHqName}
      </Anchor>
    );
  }

  // Add special case for third-party orders
  if (pathname.includes('/orders/3rd-party-orders')) {
    return (
      <Text styles={{ root: { maxWidth: '175px' } }} truncate="end">
        {selectedLocation.name}
      </Text>
    );
  }

  // checks if we're on a nested page (ex: Square under CN Integrations)
  const [, nestedParent, nestedChild] = routesBelowLayout;

  if (nestedChild?.handle && nestedParent?.handle) {
    const routesWithPaths = routesBelowLayout.filter(match => match.pathname !== '/');
    const currentRouteIndex = routesWithPaths.findIndex(match => match.pathname === pathname);
    const parentRoute = routesWithPaths[currentRouteIndex - 1];

    return (
      <Anchor underline="never" component={NavLink} to={parentRoute.pathname}>
        <Group gap="0">
          <ActionIcon>
            <ArrowLeftIconMd />
          </ActionIcon>
          <Text variant="sublabel-sm">{BACK_LABEL}</Text>
        </Group>
      </Anchor>
    );
  }

  // top level pages and location home display name
  return (
    <Group gap="sm" wrap="nowrap" maw={175}>
      <RestaurantProgress location={selectedLocation} compact link />
      <Anchor
        component={NavLink}
        to={getLocationHomePath(selectedLocation.id)}
        underline="never"
        style={{ flex: 1, minWidth: 0 }}
      >
        <Text truncate="end">{selectedLocation.name}</Text>
      </Anchor>
    </Group>
  );
}
